import type { ModeId, TargetKind, WeaponId } from '@shooting-range/shared';

type MusicThemeId = ModeId | 'menu';

interface MusicChord {
  notes: readonly number[];
  bass: number;
}

interface MusicTheme {
  bpm: number;
  gain: number;
  filterCutoff: number;
  padType: OscillatorType;
  padGain: number;
  padLength: number;
  bassType: OscillatorType;
  bassGain: number;
  bassPattern: readonly (number | null)[];
  arpType: OscillatorType;
  arpGain: number;
  arpOctave: number;
  arpPattern: readonly (number | null)[];
  chords: readonly MusicChord[];
  hatPattern?: readonly boolean[];
  hatGain?: number;
}

const MUSIC_STEPS_PER_BAR = 8;
const MUSIC_LOOKAHEAD_SECONDS = 0.35;
const LIVE_MUSIC_DUCK = 0.6;

const BASE_THEMES: Record<Exclude<MusicThemeId, 'town'>, MusicTheme> = {
  menu: {
    bpm: 88,
    gain: 0.14,
    filterCutoff: 3_800,
    padType: 'sine',
    padGain: 0.3,
    padLength: 0.96,
    bassType: 'sine',
    bassGain: 0.32,
    bassPattern: [0, null, null, null, 7, null, null, null],
    arpType: 'triangle',
    arpGain: 0.2,
    arpOctave: 12,
    arpPattern: [0, null, null, 2, null, null, 1, null],
    chords: [
      { notes: [50, 54, 57, 59], bass: 38 },
      { notes: [48, 52, 55, 59], bass: 36 },
      { notes: [43, 47, 50, 54], bass: 31 },
      { notes: [50, 54, 57, 64], bass: 38 },
      { notes: [47, 50, 54, 57], bass: 35 },
      { notes: [48, 52, 55, 59], bass: 36 },
      { notes: [43, 47, 50, 57], bass: 31 },
      { notes: [50, 54, 57, 59], bass: 38 },
    ],
  },
  classic: {
    bpm: 92,
    gain: 0.17,
    filterCutoff: 4_200,
    padType: 'triangle',
    padGain: 0.27,
    padLength: 0.94,
    bassType: 'sine',
    bassGain: 0.38,
    bassPattern: [0, null, null, null, 7, null, null, null],
    arpType: 'sine',
    arpGain: 0.27,
    arpOctave: 12,
    arpPattern: [0, null, 2, null, 1, null, 3, null],
    chords: [
      { notes: [50, 54, 57, 59], bass: 38 },
      { notes: [48, 52, 55, 59], bass: 36 },
      { notes: [43, 47, 50, 54], bass: 31 },
      { notes: [50, 54, 57, 64], bass: 38 },
      { notes: [47, 50, 54, 57], bass: 35 },
      { notes: [48, 52, 55, 59], bass: 36 },
      { notes: [43, 47, 50, 57], bass: 31 },
      { notes: [50, 54, 57, 59], bass: 38 },
    ],
  },
  popup: {
    bpm: 128,
    gain: 0.18,
    filterCutoff: 5_800,
    padType: 'sine',
    padGain: 0.2,
    padLength: 0.84,
    bassType: 'triangle',
    bassGain: 0.34,
    bassPattern: [0, null, 12, null, 0, 7, 12, null],
    arpType: 'triangle',
    arpGain: 0.33,
    arpOctave: 12,
    arpPattern: [0, 2, 1, 3, 2, 1, 3, 1],
    chords: [
      { notes: [48, 52, 55, 60], bass: 36 },
      { notes: [45, 48, 52, 57], bass: 33 },
      { notes: [41, 45, 48, 53], bass: 29 },
      { notes: [43, 47, 50, 55], bass: 31 },
      { notes: [48, 52, 55, 60], bass: 36 },
      { notes: [40, 43, 47, 52], bass: 28 },
      { notes: [41, 45, 48, 53], bass: 29 },
      { notes: [43, 47, 50, 55], bass: 31 },
    ],
  },
  attack: {
    bpm: 110,
    gain: 0.18,
    filterCutoff: 3_100,
    padType: 'triangle',
    padGain: 0.24,
    padLength: 0.78,
    bassType: 'square',
    bassGain: 0.27,
    bassPattern: [0, 0, null, 0, 12, 0, 3, 0],
    arpType: 'triangle',
    arpGain: 0.2,
    arpOctave: 12,
    arpPattern: [0, null, 1, null, 2, null, 1, 3],
    hatPattern: [false, true, false, true, false, true, false, true],
    hatGain: 0.13,
    chords: [
      { notes: [50, 53, 57, 62], bass: 38 },
      { notes: [46, 50, 53, 58], bass: 34 },
      { notes: [48, 52, 55, 60], bass: 36 },
      { notes: [45, 48, 52, 57], bass: 33 },
      { notes: [50, 53, 57, 62], bass: 38 },
      { notes: [43, 46, 50, 55], bass: 31 },
      { notes: [46, 50, 53, 58], bass: 34 },
      { notes: [45, 49, 52, 57], bass: 33 },
    ],
  },
};

const MUSIC_THEMES: Record<MusicThemeId, MusicTheme> = { ...BASE_THEMES, town: { ...BASE_THEMES.attack, bpm: 84, gain: 0.14, filterCutoff: 2_100 } };

export class SynthEngine {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private sfx: GainNode | null = null;
  private music: GainNode | null = null;
  private musicFilter: BiquadFilterNode | null = null;
  private musicTheme: MusicThemeId = 'menu';
  private activeMusicTheme: MusicThemeId | null = null;
  private musicThemeBus: GainNode | null = null;
  private musicTimer: number | null = null;
  private musicStep = 0;
  private nextMusicTime = 0;
  private percussionNoise: AudioBuffer | null = null;
  private masterValue = 0.8;
  private sfxValue = 0.9;
  private musicValue = 0.6;
  private musicDuck = 1;

  async unlock(): Promise<void> {
    if (!this.context) this.createGraph();
    if (this.context?.state === 'suspended') await this.context.resume();
    if (!this.activeMusicTheme) this.activateMusicTheme(this.musicTheme);
  }

  setMaster(value: number): void {
    this.masterValue = value;
    this.master?.gain.setTargetAtTime(value, this.context?.currentTime ?? 0, 0.025);
  }

  setSfx(value: number): void {
    this.sfxValue = value;
    this.sfx?.gain.setTargetAtTime(value, this.context?.currentTime ?? 0, 0.025);
  }

  setMusic(value: number): void {
    this.musicValue = value;
    this.music?.gain.setTargetAtTime(value * this.musicDuck, this.context?.currentTime ?? 0, 0.025);
  }

  setMusicDucked(ducked: boolean): void {
    const musicDuck = ducked ? LIVE_MUSIC_DUCK : 1;
    if (musicDuck === this.musicDuck) return;
    this.musicDuck = musicDuck;
    this.music?.gain.setTargetAtTime(this.musicValue * musicDuck, this.context?.currentTime ?? 0, 0.3);
  }

  setMusicTheme(theme: MusicThemeId): void {
    if (theme === this.musicTheme && theme === this.activeMusicTheme) return;
    this.musicTheme = theme;
    if (this.context && this.music) this.activateMusicTheme(theme);
  }

  shot(weaponId: WeaponId, pan = 0, remote = false): void {
    if (!this.context || !this.sfx) return;
    const now = this.context.currentTime;
    const destination = this.panner(pan);
    if (weaponId === 'rifle') {
      this.noiseBurst(now, 0.08, 3_200, remote ? 0.15 : 0.36, destination);
      this.thump(now, 120, 43, 0.14, remote ? 0.18 : 0.5, destination);
      this.click(now + 0.048, 1_600, destination, remote ? 0.04 : 0.12);
      this.noiseBurst(now + 0.09, 0.21, 480, remote ? 0.035 : 0.06, destination);
    } else if (weaponId === 'handgun') {
      this.noiseBurst(now, 0.115, 2_700, 0.48 * (remote ? 0.42 : 1), destination);
      this.thump(now, 105, 46, 0.16, 0.58 * (remote ? 0.4 : 1), destination);
      this.click(now + 0.072, 1_700, destination, remote ? 0.08 : 0.2);
    } else if (weaponId === 'sniper') {
      this.noiseBurst(now, 0.2, 1_800, 0.68 * (remote ? 0.42 : 1), destination);
      this.thump(now, 76, 31, 0.28, 0.82 * (remote ? 0.4 : 1), destination);
      this.noiseBurst(now + 0.13, 0.3, 520, 0.16, destination);
      this.click(now + 0.54, 1_250, destination, remote ? 0.08 : 0.26);
      this.click(now + 0.73, 920, destination, remote ? 0.06 : 0.2);
    } else {
      this.noiseBurst(now, 0.23, 1_250, 0.72 * (remote ? 0.42 : 1), destination);
      this.thump(now, 64, 28, 0.25, 0.95 * (remote ? 0.4 : 1), destination);
      this.noiseBurst(now + 0.05, 0.32, 380, 0.24, destination);
      this.click(now + 0.38, 680, destination, remote ? 0.07 : 0.25);
      this.click(now + 0.55, 1_000, destination, remote ? 0.06 : 0.18);
    }
  }

  impact(kind: TargetKind | 'miss', pan = 0): void {
    if (!this.context) return;
    const now = this.context.currentTime;
    const destination = this.panner(pan);
    if (kind === 'steel') {
      for (const [frequency, gain, decay] of [[1_430, 0.22, 0.9], [2_170, 0.12, 0.55], [3_070, 0.07, 0.34]] as const) {
        this.tone(now, frequency, frequency * 0.985, decay, gain, destination, 'sine');
      }
    } else if (kind === 'miss') {
      this.noiseBurst(now, 0.16, 340, 0.12, destination);
      this.thump(now, 62, 38, 0.09, 0.08, destination);
    } else {
      this.noiseBurst(now, 0.075, 700, 0.13, destination);
      this.thump(now, 92, 55, 0.08, 0.13, destination);
    }
  }

  destroyed(pan = 0): void {
    if (!this.context) return;
    const now = this.context.currentTime;
    const destination = this.panner(pan);
    this.tone(now, 190, 120, 0.22, 0.16, destination, 'triangle');
    this.noiseBurst(now, 0.28, 1_100, 0.17, destination);
  }

  ui(kind: 'click' | 'confirm' | 'error' = 'click'): void {
    if (!this.context || !this.sfx) return;
    const now = this.context.currentTime;
    if (kind === 'click') this.tone(now, 620, 780, 0.045, 0.08, this.sfx, 'sine');
    else if (kind === 'confirm') {
      this.tone(now, 410, 510, 0.1, 0.1, this.sfx, 'sine');
      this.tone(now + 0.07, 620, 760, 0.16, 0.08, this.sfx, 'sine');
    } else this.tone(now, 190, 125, 0.16, 0.1, this.sfx, 'square');
  }

  stinger(kind: 'start' | 'end'): void {
    if (!this.context || !this.sfx) return;
    const now = this.context.currentTime;
    const notes = kind === 'start' ? [196, 294, 392] : [392, 294, 196];
    notes.forEach((frequency, index) => this.tone(now + index * 0.1, frequency, frequency * 1.01, 0.3, 0.08, this.sfx!, 'triangle'));
    this.noiseBurst(now, 0.4, kind === 'start' ? 1_000 : 550, 0.08, this.sfx);
  }

  footstep(sprint: boolean): void {
    if (!this.context || !this.sfx) return;
    const now = this.context.currentTime;
    this.noiseBurst(now, 0.07, 500, sprint ? 0.1 : 0.055, this.sfx);
    this.thump(now, 74, 35, 0.08, sprint ? 0.13 : 0.08, this.sfx);
  }

  reload(): void {
    if (!this.context || !this.sfx) return;
    const now = this.context.currentTime;
    this.click(now, 530, this.sfx, 0.13);
    this.noiseBurst(now + 0.2, 0.18, 1_500, 0.08, this.sfx);
    this.click(now + 0.9, 840, this.sfx, 0.2);
    this.click(now + 1.15, 1_300, this.sfx, 0.12);
  }

  raiderShot(pan = 0, intensity = 1): void {
    if (!this.context || !this.sfx) return;
    const now = this.context.currentTime, destination = this.panner(pan);
    this.noiseBurst(now, 0.11, 2500, 0.42 * intensity, destination);
    this.thump(now, 130, 45, 0.18, 0.38 * intensity, destination);
    this.noiseBurst(now + 0.07, 0.25, 650, 0.1 * intensity, destination);
  }

  hurt(pan = 0): void {
    if (!this.context || !this.sfx) return;
    const destination = this.panner(pan);
    this.thump(this.context.currentTime, 80, 28, 0.25, 0.4, destination);
    this.noiseBurst(this.context.currentTime, 0.18, 360, 0.2, destination);
  }

  creature(pan = 0, intensity = 1): void {
    if (!this.context) return;
    const now = this.context.currentTime, destination = this.panner(pan);
    this.tone(now, 92, 39, 0.65, 0.08 * intensity, destination, 'sawtooth');
    this.tone(now + 0.05, 135, 57, 0.5, 0.045 * intensity, destination, 'triangle');
    this.noiseBurst(now, 0.7, 480, 0.08 * intensity, destination);
  }

  private createGraph(): void {
    this.context = new AudioContext({ latencyHint: 'interactive' });
    this.master = this.context.createGain();
    this.sfx = this.context.createGain();
    this.music = this.context.createGain();
    this.musicFilter = this.context.createBiquadFilter();
    this.master.gain.value = this.masterValue;
    this.sfx.gain.value = this.sfxValue;
    this.music.gain.value = this.musicValue * this.musicDuck;
    this.musicFilter.type = 'lowpass';
    this.musicFilter.frequency.value = MUSIC_THEMES[this.musicTheme].filterCutoff;
    this.musicFilter.Q.value = 0.4;
    this.sfx.connect(this.master);
    this.music.connect(this.musicFilter).connect(this.master);
    const compressor = this.context.createDynamicsCompressor();
    compressor.threshold.value = -15; compressor.knee.value = 15; compressor.ratio.value = 4;
    this.master.connect(compressor).connect(this.context.destination);
    // A short, generated impulse places gunfire in the surrounding world.
    const reverb = this.context.createConvolver();
    const impulse = this.context.createBuffer(2, this.context.sampleRate * 1.4, this.context.sampleRate);
    for (let channel = 0; channel < 2; channel++) {
      const data = impulse.getChannelData(channel);
      for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / data.length, 3) * 0.3;
    }
    reverb.buffer = impulse;
    const wet = this.context.createGain(); wet.gain.value = 0.13;
    this.sfx.connect(reverb).connect(wet).connect(this.master);
  }

  private activateMusicTheme(themeId: MusicThemeId): void {
    if (!this.context || !this.music || this.activeMusicTheme === themeId) return;
    const now = this.context.currentTime;
    const previousBus = this.musicThemeBus;
    if (previousBus) {
      previousBus.gain.cancelAndHoldAtTime(now);
      previousBus.gain.linearRampToValueAtTime(0.0001, now + 1);
      window.setTimeout(() => previousBus.disconnect(), 1_100);
    }
    const theme = MUSIC_THEMES[themeId];
    const bus = this.context.createGain();
    bus.gain.setValueAtTime(0.0001, now);
    bus.gain.linearRampToValueAtTime(theme.gain, now + 1);
    bus.connect(this.music);
    this.musicFilter?.frequency.setTargetAtTime(theme.filterCutoff, now, 0.3);
    this.musicThemeBus = bus;
    this.activeMusicTheme = themeId;
    this.musicStep = 0;
    this.nextMusicTime = now + 0.06;
    if (this.musicTimer === null) this.musicTimer = window.setInterval(() => this.scheduleMusic(), 125);
    this.scheduleMusic();
  }

  private scheduleMusic(): void {
    if (!this.context || !this.musicThemeBus || !this.activeMusicTheme || this.context.state !== 'running') return;
    if (document.hidden) {
      this.nextMusicTime = this.context.currentTime + 0.06;
      return;
    }
    const theme = MUSIC_THEMES[this.activeMusicTheme];
    const stepDuration = 60 / theme.bpm / 2;
    const now = this.context.currentTime;
    if (this.nextMusicTime < now - 0.05) this.nextMusicTime = now + 0.06;
    const scheduleUntil = now + MUSIC_LOOKAHEAD_SECONDS;
    while (this.nextMusicTime < scheduleUntil) {
      this.scheduleMusicStep(theme, this.musicThemeBus, this.musicStep, this.nextMusicTime, stepDuration);
      this.musicStep = (this.musicStep + 1) % (theme.chords.length * MUSIC_STEPS_PER_BAR);
      this.nextMusicTime += stepDuration;
    }
  }

  private scheduleMusicStep(theme: MusicTheme, destination: AudioNode, step: number, at: number, stepDuration: number): void {
    const barStep = step % MUSIC_STEPS_PER_BAR;
    const chord = theme.chords[Math.floor(step / MUSIC_STEPS_PER_BAR) % theme.chords.length]!;
    if (barStep === 0) {
      const duration = stepDuration * MUSIC_STEPS_PER_BAR * theme.padLength;
      chord.notes.forEach((note) => this.musicTone(at, this.midiFrequency(note), duration, theme.padGain, destination, theme.padType, 0.28));
    }
    const bassOffset = theme.bassPattern[barStep];
    if (bassOffset !== null && bassOffset !== undefined) {
      this.musicTone(at, this.midiFrequency(chord.bass + bassOffset), stepDuration * 0.72, theme.bassGain, destination, theme.bassType, 0.012);
    }
    const arpIndex = theme.arpPattern[barStep];
    if (arpIndex !== null && arpIndex !== undefined) {
      const note = chord.notes[arpIndex % chord.notes.length]! + theme.arpOctave;
      this.musicTone(at, this.midiFrequency(note), stepDuration * 0.64, theme.arpGain, destination, theme.arpType, 0.006);
    }
    if (theme.hatPattern?.[barStep]) this.musicHat(at, theme.hatGain ?? 0.1, destination);
  }

  private musicTone(at: number, frequency: number, duration: number, gainValue: number, destination: AudioNode, type: OscillatorType, attack: number): void {
    const oscillator = this.context!.createOscillator();
    const gain = this.context!.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, at);
    gain.gain.setValueAtTime(0.0001, at);
    gain.gain.exponentialRampToValueAtTime(gainValue, at + Math.min(attack, duration * 0.4));
    gain.gain.setValueAtTime(gainValue, at + Math.max(attack, duration * 0.72));
    gain.gain.exponentialRampToValueAtTime(0.0001, at + duration);
    oscillator.connect(gain).connect(destination);
    oscillator.start(at);
    oscillator.stop(at + duration + 0.02);
  }

  private musicHat(at: number, gainValue: number, destination: AudioNode): void {
    const source = this.context!.createBufferSource();
    this.percussionNoise ??= this.noiseBuffer(0.07);
    source.buffer = this.percussionNoise;
    const filter = this.context!.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.setValueAtTime(4_600, at);
    const gain = this.context!.createGain();
    gain.gain.setValueAtTime(gainValue, at);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.045);
    source.connect(filter).connect(gain).connect(destination);
    source.start(at);
    source.stop(at + 0.055);
  }

  private midiFrequency(note: number): number {
    return 440 * 2 ** ((note - 69) / 12);
  }

  private panner(value: number): AudioNode {
    const panner = this.context!.createStereoPanner();
    panner.pan.value = Math.max(-1, Math.min(1, value));
    panner.connect(this.sfx!);
    window.setTimeout(() => panner.disconnect(), 3_000);
    return panner;
  }

  private noiseBuffer(seconds: number): AudioBuffer {
    const length = Math.ceil(this.context!.sampleRate * seconds);
    const buffer = this.context!.createBuffer(1, length, this.context!.sampleRate);
    const data = buffer.getChannelData(0);
    let last = 0;
    for (let index = 0; index < length; index += 1) {
      const white = Math.random() * 2 - 1;
      last = last * 0.82 + white * 0.18;
      data[index] = white * 0.72 + last * 0.28;
    }
    return buffer;
  }

  private noiseBurst(at: number, duration: number, cutoff: number, gainValue: number, destination: AudioNode): void {
    const source = this.context!.createBufferSource();
    source.buffer = this.noiseBuffer(Math.max(0.04, duration));
    const filter = this.context!.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(cutoff, at);
    filter.frequency.exponentialRampToValueAtTime(Math.max(120, cutoff * 0.18), at + duration);
    const gain = this.context!.createGain();
    gain.gain.setValueAtTime(0.0001, at);
    gain.gain.exponentialRampToValueAtTime(gainValue, at + 0.004);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + duration);
    source.connect(filter).connect(gain).connect(destination);
    source.start(at);
    source.stop(at + duration + 0.02);
  }

  private thump(at: number, from: number, to: number, duration: number, gainValue: number, destination: AudioNode): void {
    this.tone(at, from, to, duration, gainValue, destination, 'sine');
  }

  private click(at: number, frequency: number, destination: AudioNode, gainValue: number): void {
    this.tone(at, frequency, frequency * 0.55, 0.028, gainValue, destination, 'square');
  }

  private tone(at: number, from: number, to: number, duration: number, gainValue: number, destination: AudioNode, type: OscillatorType): void {
    const oscillator = this.context!.createOscillator();
    const gain = this.context!.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(from, at);
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(20, to), at + duration);
    gain.gain.setValueAtTime(0.0001, at);
    gain.gain.exponentialRampToValueAtTime(gainValue, at + Math.min(0.008, duration * 0.2));
    gain.gain.exponentialRampToValueAtTime(0.0001, at + duration);
    oscillator.connect(gain).connect(destination);
    oscillator.start(at);
    oscillator.stop(at + duration + 0.02);
  }
}
